import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import type { Plan } from "../../Graphql/types/plan";
import { FeatureCode } from "../Features/enums/featureEnums";

interface PlanCardViewProps {
  plan: Plan;
  price?: number;
  currency?: string;
  billingCycle?: string;
  features?: Array<{
    featureCode: FeatureCode;
    name: string;
    included: boolean;
  }>;
  onSelect?: (planId: string) => void;
  isPopular?: boolean;
  color?: "blue" | "purple" | "pink";
}

const PlanCardView: React.FC<PlanCardViewProps> = ({
  plan,
  price,
  currency = "RWF",
  billingCycle = "",
  features = [],
  onSelect,
  isPopular = false,
  color = "blue",
}) => {
  const getStatusGradient = () => {
    const colorMap = {
      blue: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      purple: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      pink: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    };
    return colorMap[color];
  };

  const gradient = getStatusGradient();

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
        overflow: "visible",
        position: "relative",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Popular Badge */}
      {isPopular && (
        <Box
          sx={{
            position: "absolute",
            top: -12,
            right: 20,
            zIndex: 1,
            background: gradient,
            color: "white",
            px: 2,
            py: 0.5,
            borderRadius: 2,
            fontSize: "0.75rem",
            fontWeight: "bold",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}
        >
          Popular
        </Box>
      )}

      {/* Header with Gradient */}
      <Box
        sx={{
          background: gradient,
          color: "white",
          p: 4,
          borderRadius: "12px 12px 0 0",
          position: "relative",
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            mb: 1,
            fontSize: "15px",
          }}
        >
          {plan.name}
        </Typography>
        {price !== undefined ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ fontSize: "14px" }}
            >
              {price.toLocaleString()}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{ opacity: 0.9, fontSize: "14px" }}
            >
              {currency}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {billingCycle}
          </Typography>
        )}
      </Box>

      <CardContent
        sx={{ p: 4, display: "flex", flexDirection: "column", flexGrow: 1 }}
      >
        {/* Plan Details */}
        <Box sx={{ mb: 3 }}>
          {plan.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {plan.description}
            </Typography>
          )}
        </Box>

        {/* Features List */}
        {features && features.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2, flexGrow: 1 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ mb: 1.5, color: "text.primary" }}
              >
                Features included:
              </Typography>
              <List dense sx={{ py: 0 }}>
                {features.map((feature, index) => (
                  <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      {feature.included ? (
                        <CheckCircleIcon
                          sx={{ color: "#4caf50", fontSize: "1rem" }}
                        />
                      ) : (
                        <CancelIcon
                          sx={{ color: "#f44336", fontSize: "1rem" }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={feature.name}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: "text.secondary",
                        sx: {
                          fontSize: "0.75rem",
                          textDecoration: feature.included
                            ? "none"
                            : "line-through",
                          opacity: feature.included ? 1 : 0.6,
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Select Plan Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={() => onSelect && onSelect(plan.planId)}
          sx={{
            background: gradient,
            color: "white",
            py: 1.5,
            borderRadius: 2,
            fontWeight: "bold",
            fontSize: "0.875rem",
            "&:hover": {
              opacity: 0.9,
              transform: "scale(1.02)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Select plan
        </Button>
      </CardContent>
    </Card>
  );
};

export default PlanCardView;
